import { forwardRef, MouseEventHandler } from 'react';
import beTwMerge from '../../../utils/beTwMerge';
import { ColorKeys } from '../../../styles/colors';

const globalButtonStyle = `
  tw-px-4
  tw-min-w-[128px]
  tw-max-w-[480px]
  tw-w-full
  md:tw-w-auto
  tw-text-sm
  tw-leading-[42px]
  tw-font-medium
  tw-text-center
`;

const coreButtonStyle = beTwMerge(
  globalButtonStyle,
  `
  tw-border-0
`
);

const coreOutlinedButtonStyle = beTwMerge(
  globalButtonStyle,
  `
  tw-bg-transparent
  tw-border
  tw-border-solid
  tw-leading-[40px]
`
);

// ButtonLink: a button that looks like a link
const coreButtonLinkStyle = beTwMerge(
  globalButtonStyle,
  `
  tw-underline
  hover:tw-underline
  tw-border-0
  tw-px-0
  tw-min-w-0
  tw-max-w-none
  tw-w-auto
  tw-leading-[1.4]
  tw-text-base
`
);

// LinkButton: a link that looks like a button
const coreLinkButtonStyle = beTwMerge(`
  tw-inline-block
  tw-no-underline
  tw-leading-[44px]
  hover:tw-no-underline
  active:tw-no-underline
`);

const coreLinkOutlinedButtonStyle = beTwMerge(`
  tw-inline-block
  tw-no-underline
  tw-leading-[42px]
  hover:tw-no-underline
  active:tw-no-underline
`);

const buttonStyles = {
  primary: {
    'dark-green': beTwMerge(
      coreButtonStyle,
      `
      tw-text-color-white
      tw-bg-color-dark-green
      hover:tw-text-color-white
      hover:tw-bg-color-dark-green-light
      active:tw-text-color-white
      active:tw-bg-color-dark-green-light
    `
    ),
    white: beTwMerge(
      coreButtonStyle,
      `
      tw-text-color-black
      tw-bg-color-white
      hover:tw-text-color-white
      hover:tw-bg-color-black
      active:tw-text-color-white
      active:tw-bg-color-black
    `
    ),
    'light-green': beTwMerge(
      coreButtonStyle,
      `
      tw-text-color-white
      tw-bg-color-light-green
      hover:tw-text-color-white
      hover:tw-bg-color-light-green-light
      active:tw-text-color-white
      active:tw-bg-color-light-green-light
    `
    ),
    'light-mauve': beTwMerge(
      coreButtonStyle,
      `
      tw-text-color-black
      tw-bg-color-light-mauve
      hover:tw-text-color-black
      hover:tw-bg-color-light-mauve-dark
      active:tw-text-color-black
      active:tw-bg-color-light-mauve-dark
    `
    ),
    cream: beTwMerge(
      coreButtonStyle,
      `
      tw-text-color-black
      tw-bg-color-cream
      hover:tw-text-color-black
      hover:tw-bg-color-cream-dark
      active:tw-text-color-black
      active:tw-bg-color-cream-dark
    `
    ),
    'dark-rose': beTwMerge(
      coreButtonStyle,
      `
      tw-text-color-white
      tw-bg-color-dark-rose
      hover:tw-text-color-white
      hover:tw-bg-color-dark-rose-dark
      active:tw-text-color-white
      active:tw-bg-color-dark-rose-dark
    `
    ),
  },

  secondary: {
    'dark-green': beTwMerge(
      coreOutlinedButtonStyle,
      `
      tw-text-color-black
      tw-border-color-black
      hover:tw-text-color-white
      hover:tw-border-color-black
      hover:tw-bg-color-black
      active:tw-text-color-white
      active:tw-border-color-black
      active:tw-bg-color-black
    `
    ),
    white: beTwMerge(
      coreOutlinedButtonStyle,
      `
      tw-text-color-white
      tw-border-color-white
      hover:tw-text-color-black
      hover:tw-border-color-white
      hover:tw-bg-color-white
      active:tw-text-color-black
      active:tw-border-color-white
      active:tw-bg-color-white
    `
    ),
    black: beTwMerge(
      coreOutlinedButtonStyle,
      `
      tw-text-color-black
      tw-border-color-black
      hover:tw-text-color-black
      hover:tw-border-color-white
      hover:tw-bg-color-white
      active:tw-text-color-black
      active:tw-border-color-white
      active:tw-bg-color-white
    `
    ),
    'light-green': beTwMerge(
      coreOutlinedButtonStyle,
      `
      tw-text-color-light-green
      tw-border-color-light-green
      hover:tw-text-color-white
      hover:tw-border-color-light-green
      hover:tw-bg-color-light-green
      active:tw-text-color-white
      active:tw-border-color-light-green
      active:tw-bg-color-light-green
    `
    ),
    'light-mauve': beTwMerge(
      coreOutlinedButtonStyle,
      `
      tw-text-color-light-mauve
      tw-border-color-light-mauve
      hover:tw-text-color-black
      hover:tw-border-color-light-mauve
      hover:tw-bg-color-light-mauve
      active:tw-text-color-black
      active:tw-border-color-light-mauve
      active:tw-bg-color-light-mauve
    `
    ),
    cream: beTwMerge(
      coreOutlinedButtonStyle,
      `
      tw-text-color-cream
      tw-border-color-cream
      hover:tw-text-color-black
      hover:tw-border-color-cream
      hover:tw-bg-color-cream
      active:tw-text-color-black
      active:tw-border-color-cream
      active:tw-bg-color-cream
    `
    ),
    'dark-rose': beTwMerge(
      coreOutlinedButtonStyle,
      `
      tw-text-color-dark-rose
      tw-border-color-dark-rose
      hover:tw-text-color-white
      hover:tw-border-color-dark-rose
      hover:tw-bg-color-dark-rose
      active:tw-text-color-white
      active:tw-border-color-dark-rose
      active:tw-bg-color-dark-rose
    `
    ),
  },

  link: {
    'dark-green': beTwMerge(
      coreButtonLinkStyle,
      `
      tw-text-color-black
      tw-bg-transparent
      hover:tw-text-color-light-green-cta
      active:tw-text-color-light-green-cta
      tw-underline
    `
    ),
    white: beTwMerge(
      coreButtonLinkStyle,
      `
      tw-text-color-white
      tw-bg-transparent
      hover:tw-text-color-black
      active:tw-text-color-black
      tw-underline
    `
    ),
  },

  dtMb: {
    'dtWhite-mbBlack': beTwMerge(
      coreButtonStyle,
      `
      tw-text-color-black
      lg:tw-text-color-white
      tw-bg-transparent
      lg:hover:tw-text-color-white
      lg:hover:tw-bg-color-black
      lg:active:tw-text-color-white
      lg:active:tw-bg-color-black
      tw-border
      tw-border-solid
      tw-border-black
      lg:tw-border-white
    `
    ),
  },
};

export type ButtonProps = {
  label: string | React.ReactNode;
  type?: 'primary' | 'secondary' | 'link' | 'dtMb';
  color?: ColorKeys;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  href?: string;
  target?: string;
  className?: string;
  title?: string;
  ariaLabel?: string;
  disabled?: boolean;
  styles?: any;
};

const Button = forwardRef(
  (
    {
      label,
      type = 'primary',
      color,
      onClick = () => {},
      href,
      target = '_self',
      className,
      disabled,
      title,
      ariaLabel,
      styles = {},
    }: ButtonProps,
    ref: any
  ) => {
    // Set default color based on type
    const resolvedColor = color || (type === 'dtMb' ? 'dtWhite-mbBlack' : 'dark-green');
    const classNames = beTwMerge(buttonStyles[type][resolvedColor], className);
    const resolvedAriaLabel = ariaLabel || title || (typeof label === 'string' ? label : '');

    if (href) {
      const linkAsButtonStyles = {
        link: '',
        primary: coreLinkButtonStyle,
        secondary: coreLinkOutlinedButtonStyle,
        dtMb: coreLinkButtonStyle,
      }[type];

      return (
        <a
          href={href}
          ref={ref}
          target={target}
          className={beTwMerge(classNames, linkAsButtonStyles)}
          aria-label={`${resolvedAriaLabel} in ${href}`}
          onClick={onClick}
          style={styles}
        >
          {label}
        </a>
      );
    }

    return (
      <button
        type="button"
        ref={ref}
        className={beTwMerge(classNames, disabled && `tw-opacity-50`)}
        aria-label={resolvedAriaLabel}
        onClick={onClick}
        disabled={disabled}
        style={styles}
      >
        {label}
      </button>
    );
  }
);

export default Button;
